import React, { useState, useRef } from 'react';
import { Upload, Image, AlertCircle, CheckCircle, Loader, X, MoveUp, MoveDown } from 'lucide-react';
import axios from 'axios';

const ReceiptUpload = ({ onScanComplete, onError }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const [scanFailed, setScanFailed] = useState(false);
  const [retryStatus, setRetryStatus] = useState('');
  const scanFailedFilesRef = useRef([]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setError('');
    setScanFailed(false);
    setRetryStatus('');

    // Combine with existing files
    const newFiles = [...selectedFiles, ...files];
    setSelectedFiles(newFiles);

    // Generate previews for image files
    const newPreviews = await Promise.all(
      newFiles.map(async (file, index) => {
        if (file.type.startsWith('image/')) {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve({ index, url: e.target.result, file });
            reader.readAsDataURL(file);
          });
        } else {
          return { index, url: null, file }; // PDF or other non-image
        }
      })
    );

    setFilePreviews(newPreviews);
  };

  const compressImage = async (file) => {
    // Only compress images, not PDFs
    if (!file.type.startsWith('image/')) {
      return file;
    }

    console.log('Compressing:', file.name, 'Original size:', (file.size / 1024).toFixed(2), 'KB');

    // Detect mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onerror = () => {
        console.warn('FileReader error - returning original file');
        resolve(file);
      };

      reader.onload = (e) => {
        const img = new window.Image();

        img.onerror = () => {
          console.warn('Image load error - returning original file');
          resolve(file);
        };

        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Balanced compression for mobile to stay under iOS Safari's ~10MB limit
          // Mobile: max 1000px, 65% quality (balanced quality/size for 3+ images)
          // Desktop: max 1400px, 80% quality (high quality)
          const maxSize = isMobile ? 1000 : 1400;
          const quality = isMobile ? 0.65 : 0.8;

          let width = img.width;
          let height = img.height;

          if (width > height && width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                console.warn('Canvas toBlob failed - returning original file');
                resolve(file);
                return;
              }

              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });

              console.log('Image compressed:', {
                device: isMobile ? 'mobile' : 'desktop',
                originalSize: (file.size / 1024).toFixed(2) + ' KB',
                compressedSize: (compressedFile.size / 1024).toFixed(2) + ' KB',
                reduction: (((file.size - compressedFile.size) / file.size) * 100).toFixed(1) + '%',
                dimensions: `${width}x${height}`,
                quality: `${quality * 100}%`
              });

              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (indexToRemove) => {
    const newFiles = selectedFiles.filter((_, index) => index !== indexToRemove);
    const newPreviews = filePreviews.filter((_, index) => index !== indexToRemove);
    setSelectedFiles(newFiles);
    setFilePreviews(newPreviews);
    setError('');
    setScanFailed(false);
    setRetryStatus('');
  };

  const removeAllFiles = () => {
    setSelectedFiles([]);
    setFilePreviews([]);
    setError('');
    setScanFailed(false);
    setRetryStatus('');
    // Reset file input
    const fileInput = document.getElementById('receipt-upload');
    if (fileInput) fileInput.value = '';
  };

  const moveFile = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= selectedFiles.length) return;

    const newFiles = [...selectedFiles];
    const newPreviews = [...filePreviews];

    // Swap files
    [newFiles[index], newFiles[newIndex]] = [newFiles[newIndex], newFiles[index]];
    [newPreviews[index], newPreviews[newIndex]] = [newPreviews[newIndex], newPreviews[index]];

    setSelectedFiles(newFiles);
    setFilePreviews(newPreviews);
  };

  const handleScanReceipt = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one receipt image');
      return;
    }

    setIsScanning(true);
    setError('');
    setScanFailed(false);
    setRetryStatus('');

    // Compress all images before sending (done once, reused across retries)
    const compressedFiles = await Promise.all(
      selectedFiles.map(file => compressImage(file))
    );

    const MAX_ATTEMPTS = 3;
    const RETRY_DELAY_MS = 1500;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      if (attempt > 1) {
        setRetryStatus(`Retrying scan… (attempt ${attempt} of ${MAX_ATTEMPTS})`);
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      }

      try {
        const formData = new FormData();
        // For OCR scanning, send only the first image
        formData.append('receiptImage', compressedFiles[0]);

        const response = await axios.post('/api/receipt-scan', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data.success) {
          // Success — pass extracted data and ALL compressed files to parent
          setIsScanning(false);
          setRetryStatus('');
          onScanComplete({
            vendor: response.data.data.vendor,
            amount: response.data.data.amount,
            date: response.data.data.date,
            cardLastFour: response.data.data.cardLastFour,
            confidence: response.data.data.confidence,
            receiptImages: compressedFiles,
          });
          return;
        }

        // success: false — retry
      } catch (err) {
        console.error(`Receipt scan attempt ${attempt} failed:`, err);
      }
    }

    // All attempts exhausted — enter the manual-fallback state
    setIsScanning(false);
    setRetryStatus('');
    setScanFailed(true);
    // Stash the compressed files on the component instance so the
    // "Continue without scan" handler can reuse them without recompressing.
    scanFailedFilesRef.current = compressedFiles;
    const fallbackMessage =
      "Auto-scan is currently unavailable. You can continue without it — you'll need to enter the date, amount, and vendor manually.";
    setError(fallbackMessage);
    onError?.(fallbackMessage);
  };

  const handleContinueWithoutScan = () => {
    // Pass only the (already compressed) receipt images — no vendor/amount/date.
    // TransactionDetails will detect the absence of those fields and render the
    // yellow "Auto-fill unavailable" banner instead of the green success banner.
    onScanComplete({ receiptImages: scanFailedFilesRef.current });
  };

  return (
    <div className="space-y-4">
      {/* File Upload Area */}
      <div className="space-y-4">
        <div className="flex items-center justify-center w-full">
          <label
            htmlFor="receipt-upload"
            className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              error
                ? 'border-red-300 bg-red-50 hover:bg-red-100'
                : selectedFiles.length > 0
                  ? 'border-green-300 bg-green-50 hover:bg-green-100'
                  : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
            }`}
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              {selectedFiles.length > 0 ? (
                <>
                  <CheckCircle className="w-10 h-10 mb-3 text-green-500" />
                  <p className="mb-2 text-sm text-green-700">
                    <span className="font-semibold">{selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected</span>
                  </p>
                  <p className="text-xs text-green-600">Click to add more images</p>
                </>
              ) : (
                <>
                  <Upload className="w-10 h-10 mb-3 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">Select one or more images (PNG, JPG, or PDF up to 10MB each)</p>
                </>
              )}
            </div>
            <input
              id="receipt-upload"
              type="file"
              className="hidden"
              accept="image/*,application/pdf"
              onChange={handleFileSelect}
              multiple
            />
          </label>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
            <AlertCircle className="w-4 h-4 mr-2" />
            {error}
          </div>
        )}

        {/* Image Gallery */}
        {filePreviews.length > 0 && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm font-medium text-gray-700">
                Selected Images ({filePreviews.length}):
              </p>
              {selectedFiles.length > 1 && (
                <p className="text-xs text-gray-500">
                  Images will be combined into a single PDF (in this order)
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {filePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <div className="relative border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                    {preview.url ? (
                      <img
                        src={preview.url}
                        alt={`Receipt ${index + 1}`}
                        className="w-full h-32 object-contain"
                      />
                    ) : (
                      <div className="w-full h-32 flex items-center justify-center bg-gray-100">
                        <div className="text-center">
                          <Image className="w-8 h-8 mx-auto text-gray-400 mb-1" />
                          <p className="text-xs text-gray-500">PDF</p>
                        </div>
                      </div>
                    )}
                    <div className="absolute top-0 left-0 bg-primary-600 text-white text-xs px-2 py-1 rounded-br">
                      {index + 1}
                    </div>
                  </div>

                  {/* Control buttons */}
                  <div className="absolute top-1 right-1 flex gap-1">
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => moveFile(index, 'up')}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded p-1 text-xs transition-colors shadow"
                        title="Move up"
                      >
                        <MoveUp className="w-3 h-3" />
                      </button>
                    )}
                    {index < selectedFiles.length - 1 && (
                      <button
                        type="button"
                        onClick={() => moveFile(index, 'down')}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded p-1 text-xs transition-colors shadow"
                        title="Move down"
                      >
                        <MoveDown className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="bg-red-600 hover:bg-red-700 text-white rounded p-1 text-xs transition-colors shadow"
                      title="Remove"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>

                  <p className="text-xs text-gray-600 mt-1 truncate" title={preview.file.name}>
                    {preview.file.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={removeAllFiles}
          disabled={selectedFiles.length === 0 || isScanning}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Clear All
        </button>

        {scanFailed ? (
          <button
            type="button"
            onClick={handleContinueWithoutScan}
            className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
          >
            Continue without scan
          </button>
        ) : (
          <button
            type="button"
            onClick={handleScanReceipt}
            disabled={selectedFiles.length === 0 || isScanning}
            className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isScanning ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                {retryStatus || `Scanning Receipt${selectedFiles.length > 1 ? 's' : ''}...`}
              </>
            ) : (
              <>
                <Image className="w-4 h-4 mr-2" />
                Scan Receipt{selectedFiles.length > 1 ? 's' : ''}
              </>
            )}
          </button>
        )}
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Tips for better results</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Make sure the receipt is clearly visible and well-lit</li>
                <li>Avoid shadows or glare on the receipt</li>
                <li>Include the entire receipt in the image</li>
                <li>Keep the receipt as flat as possible</li>
                <li>Use the arrows to reorder images if needed</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptUpload;